import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CreditCard,
    Pencil,
    Plus,
} from "lucide-react";

import EntityCard from "@/framework/entity/EntityCard";
import EntityTable, {
    EntityColumn,
} from "@/framework/entity/EntityTable";
import Button from "@/framework/buttons/Button";

import {
    AdminOrderResponse,
} from "@/api";

import {
    OrderAdminService,
} from "../service/orderService";

export default function AdminOrderView() {
    const navigate = useNavigate();

    const [orders, setOrders] =
        useState<AdminOrderResponse[]>([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState<string | null>(null);

    //--------------------------------------------------
    // Load Orders
    //--------------------------------------------------

    const loadOrders = useCallback(
        async () => {
            setLoading(true);
            setError(null);

            try {
                const result =
                    await OrderAdminService.list();

                setOrders(result);

            } catch (err) {
                console.error(
                    "Failed to load orders",
                    err
                );

                setError(
                    err instanceof Error
                        ? err.message
                        : "Unable to load orders."
                );

            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        void loadOrders();
    }, [loadOrders]);

    //--------------------------------------------------
    // Navigation
    //--------------------------------------------------

    function handleNew() {
        navigate("/admin/orders/new");
    }

    function handleEdit(
        order: AdminOrderResponse
    ) {
        navigate(
            `/admin/orders/edit/${order.id}`
        );
    }

    function handleWorkflow(
        order: AdminOrderResponse
    ) {
        navigate(
            `/admin/orders/workflow/${order.id}`
        );
    }

    //--------------------------------------------------
    // Primary Action
    //--------------------------------------------------

    function renderPrimaryAction(
        order: AdminOrderResponse
    ) {
        const status =
            order.order_status?.toLowerCase();

        /*
         * ----------------------------------------------
         * CREATED
         * ----------------------------------------------
         *
         * Order is still being prepared.
         *
         * Admin can edit:
         * - Order details
         * - Order items
         * - Invoice details
         * - Commercials
         * - Payment schedule
         *
         */

        if (status === "created") {
            return (
                <Button
                    type="button"
                    variant="secondary"
                    onClick={(event) => {
                        event.stopPropagation();

                        handleEdit(order);
                    }}
                >
                    <Pencil size={15} />

                    <span>
                        Edit
                    </span>
                </Button>
            );
        }

        /*
         * ----------------------------------------------
         * POST-CREATED
         * ----------------------------------------------
         *
         * Once the invoice is approved, the order
         * moves into the operational workflow.
         *
         * The workflow page handles:
         *
         * - Payment status
         * - Payment reminders
         * - Overdue payments
         * - Confirm order
         * - Schedule order
         * - Complete order
         * - Cancel order
         *
         */

        return (
            <Button
            type="button"
            variant="secondary"
            onClick={(event) => {
                event.stopPropagation();

                navigate(`/admin/orders/${order.id}/payments`);
            }}
            >
            <CreditCard size={15} />
            <span>Payments</span>
            </Button>
        );
    }

    //--------------------------------------------------
    // Columns
    //--------------------------------------------------

    const columns: EntityColumn<AdminOrderResponse>[] = [
        {
            header: "Order #",

            render: order =>
                `#${order.id}`,

            width: "w-24",
        },

        {
            header: "Order Date",

            render: order =>
                order.order_date
                    ? new Date(
                        order.order_date
                    ).toLocaleDateString()
                    : "—",
        },

        {
            header: "Requestor",

            render: order =>
                order.requestor_name || "—",
        },

        {
            header: "Items",

            render: order =>
                order.items?.length ?? 0,

            align: "center",
            width: "w-24",
        },

        {
            header: "Status",

            render: order =>
                order.order_status?.toLocaleUpperCase() || "—",
        },

        {
            header: "Invoice",

            render: order => {
                if (order.order_status !== "invoiced") {
                    return "DRAFT";
                }

                return order.invoice?.invoice_number ?? "—";
            },
        },

        {
            header: "Amount",

            render: order =>
                order.invoice?.final_amount != null
                    ? `₹${Number(order.invoice.final_amount).toFixed(2)}`
                    : "—",

            align: "right",
        },

        {
            header: "Actions",

            render: order => (
                <div className="flex justify-end gap-2">
                    {renderPrimaryAction(order)}
                </div>
            ),

            align: "right",
            width: "w-40",
        },
    ];

    //--------------------------------------------------
    // Row Click
    //--------------------------------------------------

    function handleRowClick(
        order: AdminOrderResponse
    ) {
        const status =
            order.order_status?.toLowerCase();

        if (status === "created") {
            handleEdit(order);
            return;
        }

        handleWorkflow(order);
    }

    //--------------------------------------------------
    // Render
    //--------------------------------------------------

    return (
        <div className="space-y-6">
            <EntityCard
                title="Orders"
                subtitle="Manage orders, invoices and payment status."
                actions={
                    <Button onClick={handleNew}>
                        <Plus size={16} />

                        <span>
                            New
                        </span>
                    </Button>
                }
            >
                {error && (
                    <div
                        className="
                            mb-4
                            rounded-lg
                            border
                            border-red-200
                            bg-red-50
                            px-4
                            py-3
                            text-sm
                            text-red-700
                        "
                    >
                        {error}
                    </div>
                )}

                {loading ? (
                    <div
                        className="
                            py-10
                            text-center
                            text-sm
                            text-slate-500
                        "
                    >
                        Loading orders...
                    </div>
                ) : (
                    <EntityTable
                        items={orders}
                        columns={columns}
                        getKey={order => order.id}
                        emptyMessage="You don't have any orders yet."
                        onRowClick={handleRowClick}
                    />
                )}
            </EntityCard>
        </div>
    );
}